import { createSlice } from '@reduxjs/toolkit';
import socket from '../../utils/socket';
import {
  ClientFilesPropTypes,
  CompanyFilesPropTypes,
  LogoPropTypes,
  UserResourceFilesPropTypes
} from '../types/filePropTypes';

const initialState = {
  isLoading: false,
  error: false,
  planSite: null,
  // datas
  logo: socket.env.logo,
  logoCompany: null,
  clientFiles: [],
  companyFiles: [],
  userResourceFiles: [],
  resourceFiles: [],
  contractScheduleFiles: [],
  siteFiles: [],
  siteScheduleFiles: [],
  orderFiles: [],
  unseenResourceFilesCountGlobal: 0,
  unseenResourceFilesCountPerResource: 0,
  resourceSiteFileUnseen: 0,
  unseenFilesSiteCount: 0,
  unseenCompanyFiles: 0,
  unseenClientFiles: 0,
  unseenPartnerFiles: 0,
  unseenFilesCountBySite: 0,
  unseenFilesSite: [],
  locationKey: '', // current location ID
  oldLocationKey: '' //old location ID
};

const slice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getLogo(state, action) {
      state.logo = action.payload;
    },

    getLogoCompany(state, action) {
      state.logoCompany = action.payload;
      state.isLoading = false;
    },

    getClientFiles(state, action) {
      state.isLoading = false;
      state.clientFiles = action.payload.files;
      state.locationKey = action.payload.locationKey;
    },

    getCompanyFiles(state, action) {
      state.isLoading = false;
      state.companyFiles = action.payload.files;
      state.locationKey = action.payload.locationKey;
    },

    getUserResourceFiles(state, action) {
      state.isLoading = false;
      state.userResourceFiles = action.payload.files;
      state.locationKey = action.payload.locationKey;
    },

    getResourceFiles(state, action) {
      state.isLoading = false;
      state.resourceFiles = action.payload.files;
      state.locationKey = action.payload.locationKey;
      state.unseenResourceFilesCountPerResource = action.payload.files.filter(
        (file) => (!file.viewed_at || !file.validated_at) && (file.viewed_at === null || file.validated_at === null)
      ).length;
    },

    updateUserDocumentStatus(state, action) {
      const { viewedAtUpdate, validatedAtUpdate, isValidationRequired = false, fileKeyword = null } = action.payload;
      const shouldDecrement = validatedAtUpdate || (viewedAtUpdate && !isValidationRequired);

      const decrementStateProperties = (state, keysToDecrement) => {
        keysToDecrement.forEach((key) => {
          if (shouldDecrement) {
            state[key] = Math.max(0, state[key] - 1);
          }
        });
      };

      const getFiles = (filesList) => {
        return filesList.map((file) => {
          const newFile = { ...file };
          if (file.id === action.payload.selectedFileId) {
            newFile.viewed_at = action.payload.timestamp;
            if (action.payload.validatedAtUpdate) {
              newFile.validated_at = action.payload.timestamp;
            }
          }
          return newFile;
        });
      };

      switch (state.locationKey) {
        case 'sheet-nav':
          decrementStateProperties(state, ['unseenResourceFilesCountGlobal']);
          state.userResourceFiles = getFiles(state.userResourceFiles);
          break;

        case 'agents-nav':
          // Si une validation est requise mais que le fichier n'est pas encore validé,
          // on met à jour seulement la liste des fichiers sans décrémenter les badges.
          if (action.payload.isValidationRequired && !action.payload.validatedAtUpdate) {
            state.resourceFiles = getFiles(state.resourceFiles);
          } else {
            state.unseenResourceFilesCountPerResource = Math.max(0, state.unseenResourceFilesCountPerResource - 1);
            state.unseenResourceFilesCountGlobal = Math.max(0, state.unseenResourceFilesCountGlobal - 1);
            state.resourceFiles = getFiles(state.resourceFiles);
          }
          break;

        case 'planning-nav':
          decrementStateProperties(state, ['resourceSiteFileUnseen', 'unseenFilesSiteCount']);
          state.siteFiles = getFiles(state.siteFiles);
          state.unseenFilesSite = getFiles(state.unseenFilesSite);
          break;

        case 'planning-nav-alert-files':
          decrementStateProperties(state, ['unseenFilesSiteCount']);
          state.unseenFilesSite = getFiles(state.unseenFilesSite);
          break;

        case 'sites-list-nav':
          {
            state.unseenFilesSiteCount = Math.max(0, state.unseenFilesSiteCount - 1);
            state.unseenFilesSite = getFiles(state.unseenFilesSite);
            const isInSiteFiles = state.siteFiles.some((file) => file.id === action.payload.selectedFileId);
            if (isInSiteFiles) {
              decrementStateProperties(state, ['unseenFilesCountBySite']);
              state.siteFiles = getFiles(state.siteFiles);
            }
          }
          break;

        // Cas 'manager-sites-list-nav' : Gère la navigation ou l'interaction avec la liste des sites dans le gestionnaire.
        // 1. Décrémente le compteur `unseenFilesSiteCount` (nombre de fichiers non vus par site), en s'assurant qu'il ne devienne pas négatif grâce à `Math.max(0, ...)`.
        // 2. Met à jour la liste des fichiers non vus (`unseenFilesSite`) en appelant la fonction `getFiles`, qui semble filtrer ou transformer les données.
        // 3. Vérifie si le fichier sélectionné (`action.payload.selectedFileId`) existe déjà dans la liste des fichiers du site actuel (`state.siteFiles`).
        //    - Si c'est le cas, décrémente certaines propriétés d'état via la fonction `decrementStateProperties` (probablement un utilitaire pour réduire des compteurs spécifiques).
        //    - Met également à jour la liste des fichiers du site (`state.siteFiles`) en appelant `getFiles`.
        // Ce code garantit que les compteurs et les listes de fichiers sont correctement synchronisés lorsque l'utilisateur navigue ou interagit avec les sites.

        case 'manager-sites-list-nav':
          {
            state.unseenFilesSiteCount = Math.max(0, state.unseenFilesSiteCount - 1);
            state.unseenFilesSite = getFiles(state.unseenFilesSite);
            const isInSiteFiles = state.siteFiles.some((file) => file.id === action.payload.selectedFileId);
            if (isInSiteFiles) {
              decrementStateProperties(state, ['unseenFilesCountBySite']);
              state.siteFiles = getFiles(state.siteFiles);
            }
          }
          break;

        //Mise à jour du statut du document consulté et la pastille pour rôle client
        case 'client-sites-list-nav':
          state.unseenFilesCountBySite = Math.max(0, state.unseenFilesCountBySite - 1);
          state.unseenFilesSiteCount = Math.max(0, state.unseenFilesSiteCount - 1);
          state.siteFiles = getFiles(state.siteFiles);
          break;

        case 'company-docs-list':
          state.unseenCompanyFiles = Math.max(0, state.unseenCompanyFiles - 1);
          state.companyFiles = getFiles(state.companyFiles);
          break;

        case 'partners-docs-list':
          state.unseenPartnerFiles = Math.max(0, state.unseenPartnerFiles - 1);
          state.companyFiles = getFiles(state.companyFiles);
          break;

        case 'client-docs-list':
          decrementStateProperties(state, ['unseenClientFiles']);
          state.clientFiles = getFiles(state.clientFiles);
          break;
        default:
          if (fileKeyword === 'Agent') {
            state.resourceFiles = getFiles(state.resourceFiles);
          } else {
            console.error('updateUserDocumentStatus-UNKNOWN-LOCATION', state.locationKey);
          }
      }
    },

    getContractScheduleFiles(state, action) {
      state.isLoading = false;
      state.contractScheduleFiles = action.payload.sort((a, b) => {
        const nameA = a.name.split('_')[2];
        const nameB = b.name.split('_')[2];
        if (nameA > nameB) {
          return -1;
        }
        if (nameB > nameA) {
          return 1;
        }
        return 0;
      });
    },

    getSiteFiles(state, action) {
      state.siteFiles = action.payload.files;
      const [plan] = action.payload.files.filter((file) => file.folder === 'plans');
      state.planSite = plan || null;
      state.resourceSiteFileUnseen = action.payload.siteFileUnseen;
      state.isLoading = false;
      state.locationKey = action.payload.locationKey;
      state.unseenFilesCountBySite = state.siteFiles.filter((file) => file.viewed_at === null).length;
    },

    getSiteScheduleFiles(state, action) {
      state.isLoading = false;
      state.siteScheduleFiles = action.payload;
    },

    getOrderFiles(state, action) {
      state.isLoading = false;
      state.orderFiles = action.payload;
    },

    getResourceFilesNotifications(state, action) {
      state.unseenResourceFilesCountGlobal = action.payload.unseenResourceFilesCountGlobal;
      state.unseenFilesSiteCount = action.payload.unseenFilesSiteCount;
      state.unseenCompanyFiles = action.payload.unseenCompanyFiles;
      state.unseenClientFiles = action.payload.unseenClientFiles;
      state.unseenPartnerFiles = action.payload.unseenPartnerFiles;
    },

    getUnseenFilesSite(state, action) {
      state.unseenFilesSite = action.payload.files;
      state.locationKey = action.payload.locationKey;
    },
    updateLocationKeys(state, action) {
      if (action.payload.locationKey) state.locationKey = action.payload.locationKey;

      if (action.payload.oldLocationKey) state.oldLocationKey = action.payload.oldLocationKey;
    }
  }
});

// Reducer
export default slice.reducer;

export function getLogo(companyId = null) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getLogoCompany(''));
      const result = await socket.get('/file/logo', { companyId });

      if (result.logo) {
        if (companyId) {
          dispatch(slice.actions.getLogoCompany(result.logo));
        } else {
          dispatch(slice.actions.getLogo(result.logo));
        }
      } else {
        dispatch(slice.actions.getLogoCompany(''));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getClientFiles(ownerId, ownerIds = null, locationKey) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/files', { type: 1, ownerId, ownerIds });
      dispatch(slice.actions.getClientFiles({ files: result.files, locationKey }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCompanyFiles(ownerId, typePartnerDemandId = null, locationKey) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/files', { type: 2, ownerId, typePartnerDemandId });
      dispatch(slice.actions.getCompanyFiles({ files: result.files, locationKey }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUserResourceFiles(ownerId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/files', { entities: ['agent', 'company'], ownerId });
      const files = result.files.map((el) => getOwnerDocument(el));
      dispatch(slice.actions.getUserResourceFiles({ files: files, locationKey: 'sheet-nav' }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateUserDocumentStatus(
  {
    selectedFileId,
    timestamp,
    viewedAtUpdate = false,
    validatedAtUpdate = false,
    isValidationRequired = false,
    fileKeyword = null
  },
  callback
) {
  return async (dispatch) => {
    try {
      if (viewedAtUpdate && !validatedAtUpdate) {
        const viewedResult = await socket.put('/file/viewed', { file_id: selectedFileId, viewed_at: timestamp });
        if (viewedResult.success) {
          dispatch(
            slice.actions.updateUserDocumentStatus({
              selectedFileId,
              viewedAtUpdate,
              validatedAtUpdate,
              timestamp,
              isValidationRequired,
              fileKeyword
            })
          );
          callback('Document vu', viewedResult);
        } else {
          callback("Échec de la mise à jour du statut 'viewed_at'", viewedResult);
        }
      }

      if (validatedAtUpdate) {
        const validatedResult = await socket.put('/file/validated', {
          file_id: selectedFileId,
          validated_at: timestamp
        });
        if (validatedResult.success) {
          dispatch(
            slice.actions.updateUserDocumentStatus({ selectedFileId, viewedAtUpdate, validatedAtUpdate, timestamp })
          );
          callback('Document validé', validatedResult);
        } else {
          callback("Échec de la mise à jour du statut 'validated_at'", validatedResult);
        }
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getResourceFiles(ownerId, typePartnerDemandId = null, locationKey = null) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/files', { type: 0, ownerId, typePartnerDemandId });
      dispatch(slice.actions.getResourceFiles({ files: result.files, locationKey }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSiteFiles(ownerId, locationKey = null) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/files', { type: 3, ownerId });
      const files = result.files.map((file) => {
        if (file.folder === 'plans') {
          file.name = 'Plan du site';
        }
        return file;
      });

      // Comptage pour l’onglet Site du planning :
      const siteFileUnseen = files.filter((file) =>
        'validated_at' in file // a un workflow de validation ?
          ? file.validated_at === null
          : file.viewed_at === null
      ).length;

      dispatch(slice.actions.getSiteFiles({ files: files, siteFileUnseen: siteFileUnseen, locationKey: locationKey }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getContractScheduleFiles({ ownerId, month }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/storage/files', {
        folder: `schedules\\contracts`,
        like: `${ownerId}_${month}_`
      });
      dispatch(slice.actions.getContractScheduleFiles(result.files));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSiteScheduleFiles({ ownerId, month }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/storage/files', {
        folder: `schedules\\sites`,
        like: `${ownerId}_${month}`
      });
      dispatch(slice.actions.getSiteScheduleFiles(result.files));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getOrderFiles(ownerId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/files', { ownerId, purchaseOrderId: ownerId });
      dispatch(slice.actions.getOrderFiles(result.files));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getFileData(filepath) {
  return new Promise((resolve, reject) => {
    socket
      .get('/file/data', { filepath })
      .then((result) => {
        // CW-22029 : on rejette aussi quand `result.file` est absent meme si
        // `success: true`. Cas observe sur la page Factures pour un client
        // sans site : l'API repondait sans `file`, ce que les appelants (cf.
        // OpenDocument) traitaient comme une reussite -> setFileItem(undefined)
        // -> crash Object.keys plus bas. La couche service garantit desormais
        // que si elle resolve, c'est avec un `file` exploitable.
        if (!result.success || !result.file) {
          return reject(new Error(result.message || 'Fichier introuvable'));
        }
        resolve(result);
      })
      .catch(reject);
  });
}

export function getResourceFilesNotifications() {
  return async (dispatch) => {
    try {
      const result = await socket.get('/files/count');
      dispatch(
        slice.actions.getResourceFilesNotifications({
          unseenResourceFilesCountGlobal: result?.unseenResourceFiles,
          unseenFilesSiteCount: result?.unseenSiteFiles,
          unseenCompanyFiles: result?.unseenCompanyFiles,
          unseenClientFiles: result?.unseenClientFiles,
          unseenPartnerFiles: result?.unseenPartnerFiles
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUnseenFilesSite(locationKey = null) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/files/unseen', { entity: 'site' });
      dispatch(slice.actions.getUnseenFilesSite({ files: result.files, locationKey: locationKey }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// MAJ de current location ID et old location ID ou bien l'un de deux
export function updateLocationKeys(locationKey = null, oldLocationKey = null) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.updateLocationKeys({ locationKey: locationKey, oldLocationKey: oldLocationKey }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// export function upload(form) {
//   return async (dispatch) => {
//     dispatch(slice.actions.startLoading());
//     try {
//       const result = await socket.get('/files', form);
//       dispatch(slice.actions.upload(result.files));
//     } catch (error) {
//       dispatch(slice.actions.hasError(error));
//     }
//   };
// }

const getOwnerDocument = (el) => {
  if (el.type === 'Société') {
    el.type = el.company_name;
  }
  return el;
};

getClientFiles.propTypes = ClientFilesPropTypes;
getCompanyFiles.propTypes = CompanyFilesPropTypes;
getUserResourceFiles.propTypes = UserResourceFilesPropTypes;
getLogo.propTypes = LogoPropTypes;
